import React, { useCallback, useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { ChevronDown } from "lucide-react";

import Navbar from "./components/Navbar";
import HeroTop from "./components/HeroTop";
import Hero from "./components/Hero";
import TrustedBy from "./components/TrustedBy";
import EngineeringExcellence from "./components/EngineeringExcellence";
import Products from "./components/Products";
import InteractiveComparison from "./components/InteractiveComparison";
import Testimonials from "./components/Testimonials";
import OrderingProcess from "./components/OrderingProcess";
import FAQ from "./components/FAQ";

import Footer from "./components/Footer";
import QuoteDialog from "./components/QuoteDialog";
import CartDrawer from "./components/CartDrawer";
import { CartProvider } from "./context/CartContext";
import { AdminAuthProvider } from "./context/AdminAuthContext";
import AdminRoute from "./components/admin/AdminRoute";

// Admin pages
import AdminLogin from "./admin/pages/Login";
import AdminDashboard from "./admin/pages/Dashboard";
import AdminProducts from "./admin/pages/Products";
import AdminProductForm from "./admin/pages/ProductForm";
import AdminCategories from "./admin/pages/Categories";
import AdminContent from "./admin/pages/Content";
import AdminSettings from "./admin/pages/Settings";
import AdminQuotes from "./admin/pages/Quotes";
import AdminContacts from "./admin/pages/Contacts";
import CategoryPage from "./pages/CategoryPage";
import ProductPage from "./pages/ProductPage";
import { CATEGORY_SEO } from "./data/seoPages";

// Scroll to top on every route change (SPA keeps scroll position by default)
const ScrollToTop = () => {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, [pathname]);
    return null;
};

const Landing = () => {
    const [quoteOpen, setQuoteOpen] = useState(false);
    const [quoteProduct, setQuoteProduct] = useState("");
    const [showScrollArrow, setShowScrollArrow] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.scrollY || document.documentElement.scrollTop;
            const scrollHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
            const ratio = scrollHeight > 0 ? scrollTop / scrollHeight : 0;
            setShowScrollArrow(ratio > 0.5 && ratio < 0.98);
        };

        handleScroll();
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const scrollToBottom = () => {
        window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: "smooth",
        });
    };

    const openQuote = useCallback((product = "") => {
        setQuoteProduct(product || "");
        setQuoteOpen(true);
    }, []);

    return (
        <div className="App bg-[#FBFBFD] text-neutral-900 antialiased overflow-x-hidden">
            <Navbar onQuoteClick={() => openQuote()} />
            <HeroTop />
            <main>
                <Hero onQuoteClick={() => openQuote()} />
                <Products />
                <TrustedBy />
                <EngineeringExcellence />
                <InteractiveComparison />
                <Testimonials />
                <OrderingProcess />
                <FAQ />
            </main>
            <Footer />

            <QuoteDialog
                open={quoteOpen}
                onOpenChange={setQuoteOpen}
                defaultProduct={quoteProduct}
            />
            <CartDrawer />
            <Toaster richColors position="bottom-right" />

            {showScrollArrow && (
                <button
                    type="button"
                    onClick={scrollToBottom}
                    aria-label="Descendre en bas de la page"
                    className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 w-12 h-12 rounded-full bg-[#1C1C1E]/90 text-white flex items-center justify-center shadow-xl hover:bg-[#1C1C1E] transition"
                >
                    <ChevronDown className="h-6 w-6 animate-bounce" />
                </button>
            )}
        </div>
    );
};

function App() {
    return (
        <CartProvider>
            <AdminAuthProvider>
                <BrowserRouter>
                    <ScrollToTop />
                    <Routes>
                        <Route path="/" element={<Landing />} />
                        {Object.values(CATEGORY_SEO).map((seo) => (
                            <Route
                                key={seo.slug}
                                path={seo.path}
                                element={<CategoryPage seo={seo} />}
                            />
                        ))}
                        <Route path="/produit/:slug" element={<ProductPage />} />
                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route
                            path="/admin/dashboard"
                            element={
                                <AdminRoute>
                                    <AdminDashboard />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/products"
                            element={
                                <AdminRoute>
                                    <AdminProducts />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/products/new"
                            element={
                                <AdminRoute>
                                    <AdminProductForm />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/products/:id/edit"
                            element={
                                <AdminRoute>
                                    <AdminProductForm />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/categories"
                            element={
                                <AdminRoute>
                                    <AdminCategories />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/content"
                            element={
                                <AdminRoute>
                                    <AdminContent />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/settings"
                            element={
                                <AdminRoute>
                                    <AdminSettings />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/quote-requests"
                            element={
                                <AdminRoute>
                                    <AdminQuotes />
                                </AdminRoute>
                            }
                        />
                        <Route
                            path="/admin/contacts"
                            element={
                                <AdminRoute>
                                    <AdminContacts />
                                </AdminRoute>
                            }
                        />
                        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
                    </Routes>
                </BrowserRouter>
            </AdminAuthProvider>
        </CartProvider>
    );
}

export default App;
